/**
 * PATH: packages/email-hub/src/feature/components/EmailTagChips.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-EMAILTAGCHIPS-001
 * PURPOSE: Interactive chips for an email's `tags` (the coloured
 *          "Sydney" / "Elite Pools" labels — the org/COMPANY tier of
 *          labels, shared across the org). Each assigned tag can be
 *          removed; a "+ Tag" trigger opens a picker of the org tag
 *          catalog to add/swap AND to create a brand-new company tag
 *          inline. Left-click OR right-click on a chip opens the picker.
 * WHY:     Operator 2026-06-21: "it tagged sydney — I can't right-click
 *          and change the tag" (now fixed) AND "I can't create a label in
 *          the company". The catalog endpoint `POST /api/emails/tags/`
 *          (useEmailTags.createTag) already shipped, but the only UI to
 *          reach it was Settings → Tags — there was no way to mint a
 *          shared label from where you actually tag an email. This adds an
 *          inline "New tag" creator (name + colour) that creates the org
 *          tag and assigns it in one motion, mirroring the Personal
 *          category inline-create on CategoryChip.
 * FLOW:
 *   ┌──────────────┐  click/  ┌─────────────────────┐  toggle ┌──────────┐
 *   │ tag chip (×) │  r-click │ catalog picker      │ ──────▶ │ assign / │
 *   │ + "+ Tag"    │ ───────▶ │ (✓ = assigned)      │         │ removeTag│
 *   └──────────────┘          │ + "New tag" creator │ create+ │ createTag│
 *                             └─────────────────────┘ assign  │ +assign  │
 *                                                              └──────────┘
 * DEPENDENCIES:
 *   - ../hooks/useEmails useEmailTags: catalog + create/assign/removeTag.
 *   - ../../_internal/components/Toast: success/error feedback.
 * RELATED:
 *   - components/EmailPreviewPane.tsx + components/EmailReader.tsx: the
 *     two detail surfaces that consume this (was the read-only span map).
 *   - components/CategoryChip.tsx: sibling inline-create for the PERSONAL
 *     category tier (same create-then-apply pattern).
 *   - pages/TagsRulesPage.tsx: full org-level tag CRUD (rename / recolour).
 */

import { useEffect, useRef, useState } from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import { Check, Plus, Tag, X } from 'lucide-react'

import { cn } from '../../_internal/lib/utils'
import { useToast } from '../../_internal/components/Toast'
import { AnchoredPopover } from '../../_internal/components/AnchoredPopover'
import { useEmailTags } from '../hooks/useEmails'

export interface EmailTag {
  id: number
  name: string
  color: string
}

/** Default colour choices for a freshly-minted company tag — the same
 *  hues the category dots use so a new shared label reads consistently
 *  across the app. The first is the default selection. */
const TAG_PALETTE = [
  '#6366f1', '#22c55e', '#f97316', '#06b6d4',
  '#ec4899', '#f59e0b', '#8b5cf6', '#ef4444',
] as const

interface EmailTagChipsProps {
  emailId: string
  /** The tags currently assigned to this email (`email.tags`). */
  tags: EmailTag[] | undefined
  /** Refetch the email after a tag is added/removed so the chips
   *  repaint from the source of truth. */
  onChanged?: () => void
  /** Compact (`xs`) for the preview-pane header; `sm` for the reader. */
  size?: 'xs' | 'sm'
}

/**
 * Interactive tag chips. Renders nothing structural when there are no
 * assigned tags AND no catalog yet — but always offers the "+ Tag"
 * trigger so an operator can add the first tag from here.
 */
export function EmailTagChips({
  emailId,
  tags,
  onChanged,
  size = 'sm',
}: EmailTagChipsProps) {
  // Optimistic assigned-tags override (P2 "feels broken" sweep). The chips
  // render from the parent's `tags` prop, so adding/removing one previously
  // only repainted after the post-mutation refetch (a full round-trip of lag).
  // We mirror the assigned list locally, mutate it in the same frame as the
  // click, then drop the override the instant fresh `tags` arrive (or on
  // failure) so it never goes stale.
  const [override, setOverride] = useState<EmailTag[] | null>(null)
  useEffect(() => {
    setOverride(null)
  }, [tags])
  const assigned = override ?? tags ?? []
  const { tags: catalog, assignTag, removeTag, createTag } = useEmailTags()
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)
  // Inline "create a company tag" state — mirrors CategoryChip's personal
  // creator so both label tiers feel the same.
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState<string>(TAG_PALETTE[0])
  const [saving, setSaving] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  // BUG-2026-07-17-TAG-PICKER-CLIPPED — the picker is body-portalled
  // (see AnchoredPopover), so click-away must also treat clicks INSIDE
  // the portal as inside.
  const popoverRef = useRef<HTMLDivElement | null>(null)

  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(open, () => setOpen(false), [containerRef, popoverRef])

  // Reset the create sub-form whenever the picker closes so it never
  // re-opens mid-typed.
  useEffect(() => {
    if (!open) {
      setCreating(false)
      setNewName('')
      setNewColor(TAG_PALETTE[0])
    }
  }, [open])

  const assignedIds = new Set(assigned.map((t) => t.id))
  const busy = busyId != null || saving

  const remove = async (tagId: number, name: string) => {
    if (busy) return
    setBusyId(tagId)
    // Optimistic: drop the chip now (revert on failure).
    setOverride((prev) => (prev ?? tags ?? []).filter((t) => t.id !== tagId))
    try {
      await removeTag(emailId, tagId)
      toast(`Removed “${name}”`, 'success')
      // BL-1/2: optimistic override already paints — do not parent-refetch inbox.
    } catch (err) {
      setOverride(null)
      toast(`Could not remove tag: ${(err as Error).message || 'error'}`, 'error')
    } finally {
      setBusyId(null)
    }
  }

  const toggle = async (tag: EmailTag) => {
    if (busy) return
    setBusyId(tag.id)
    const isOn = assignedIds.has(tag.id)
    // Optimistic: flip the chip's presence in the same frame as the click.
    setOverride((prev) => {
      const curr = prev ?? tags ?? []
      return isOn ? curr.filter((t) => t.id !== tag.id) : [...curr, tag]
    })
    try {
      if (isOn) {
        await removeTag(emailId, tag.id)
        toast(`Removed “${tag.name}”`, 'success')
      } else {
        await assignTag(emailId, tag.id)
        toast(`Tagged “${tag.name}”`, 'success')
      }
      // BL-1/2: override already reflects assign/remove — skip inbox refetch.
    } catch (err) {
      setOverride(null)
      toast(`Could not update tag: ${(err as Error).message || 'error'}`, 'error')
    } finally {
      setBusyId(null)
    }
  }

  // Create a NEW org/company tag and assign it to this email in one
  // motion. createTag → assignTag → repaint. Org-shared: every teammate
  // sees the new label in their catalog after this.
  const createAndAssign = async () => {
    const name = newName.trim()
    if (!name || saving) return
    setSaving(true)
    try {
      const tag = await createTag(name.slice(0, 50), newColor)
      // Optimistic: show the freshly-minted chip before the assign round-trip.
      setOverride((prev) => [...(prev ?? tags ?? []), tag])
      await assignTag(emailId, tag.id)
      toast(`Created & tagged “${tag.name}”`, 'success')
      setOpen(false)
      // Catalog cache updated in useEmailTags; chip override paints locally.
    } catch (err) {
      setOverride(null)
      toast(`Could not create tag: ${(err as Error).message || 'error'}`, 'error')
    } finally {
      setSaving(false)
    }
  }

  const dims = size === 'xs' ? 'text-[9px] px-1.5 py-0.5' : 'text-[11px] px-2 py-0.5'
  const iconDim = size === 'xs' ? 'h-2 w-2' : 'h-2.5 w-2.5'

  // Open the manage picker. Used by both left-click and right-click on a
  // chip (the operator's explicit ask was "right-click and change").
  const openPicker = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setOpen((o) => !o)
  }

  return (
    <div ref={containerRef} className="relative inline-flex min-w-0 items-center gap-1 flex-wrap">
      {assigned.map((tag) => (
        <span
          key={tag.id}
          className={cn(
            'group inline-flex items-center gap-1 rounded-full text-white font-medium',
            dims,
            busyId === tag.id && 'opacity-50',
          )}
          style={{ backgroundColor: tag.color }}
          title="Click or right-click to change · × to remove"
          onClick={openPicker}
          onContextMenu={openPicker}
        >
          <span className="truncate max-w-[120px]">{tag.name}</span>
          <button
            type="button"
            disabled={busy}
            aria-label={`Remove tag ${tag.name}`}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              void remove(tag.id, tag.name)
            }}
            className="opacity-70 hover:opacity-100 transition-opacity"
          >
            <X className={iconDim} aria-hidden />
          </button>
        </span>
      ))}

      <button
        type="button"
        data-testid="email-tags-add"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Add or change tags"
        onClick={openPicker}
        onContextMenu={openPicker}
        className={cn(
          'inline-flex items-center gap-0.5 rounded-full border border-dashed border-border',
          'text-muted-foreground hover:border-indigo-300 hover:text-indigo-600',
          'dark:hover:text-indigo-300 transition-colors',
          dims,
        )}
      >
        <Plus className={iconDim} aria-hidden />
        <span>Tag</span>
      </button>

      {/* Body-portalled so the Signal Strip's overflow clipping (or any
          other ancestor) can never truncate the picker
          (BUG-2026-07-17-TAG-PICKER-CLIPPED). The `open &&` guard keeps
          the picker tree lazily built — this chip renders per list row. */}
      {open && (
      <AnchoredPopover
        anchorRef={containerRef}
        open={open}
        popoverRef={popoverRef}
        role="menu"
        className="min-w-[180px] overflow-y-auto rounded-md border border-border bg-card shadow-lg p-1"
      >
          <p className="px-2.5 pb-0.5 pt-1 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground/70">
            Company tags
          </p>
          <p className="px-2.5 pb-1 text-[9px] text-muted-foreground/60">
            Shared across this organisation — not personal categories or AI status
          </p>
          {catalog.length === 0 ? (
            <p className="px-2.5 py-1.5 text-[11px] text-muted-foreground">
              No tags yet - create your first below.
            </p>
          ) : (
            catalog.map((tag) => {
              const isOn = assignedIds.has(tag.id)
              return (
                <button
                  key={tag.id}
                  type="button"
                  disabled={busy}
                  onClick={() => void toggle(tag)}
                  className={cn(
                    'flex items-center gap-2 w-full px-2.5 py-1 text-[11px] text-left rounded transition-colors',
                    'hover:bg-accent disabled:opacity-50',
                    isOn && 'font-semibold',
                  )}
                  role="menuitemcheckbox"
                  aria-checked={isOn}
                >
                  <span
                    className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: tag.color }}
                  />
                  <span className="flex-1 truncate">{tag.name}</span>
                  {isOn && <Check className="h-3 w-3 text-indigo-500 shrink-0" aria-hidden />}
                </button>
              )
            })
          )}

          {/* Create a new COMPANY tag inline (shared across the org). */}
          <div className="mt-1 border-t border-border pt-1">
            {creating ? (
              <div className="px-2 py-1 space-y-1.5">
                <div className="flex items-center gap-1">
                  <input
                    autoFocus
                    data-testid="new-tag-name-input"
                    value={newName}
                    disabled={saving}
                    maxLength={50}
                    placeholder="New tag name"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        void createAndAssign()
                      } else if (e.key === 'Escape') {
                        e.preventDefault()
                        setCreating(false)
                        setNewName('')
                      }
                    }}
                    className="flex-1 min-w-0 rounded border border-border bg-background px-1.5 py-0.5 text-[11px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                  <button
                    type="button"
                    data-testid="new-tag-add"
                    disabled={saving || !newName.trim()}
                    onClick={() => void createAndAssign()}
                    className="rounded bg-indigo-600 px-2 py-0.5 text-[10px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
                <div className="flex items-center gap-1" role="radiogroup" aria-label="Tag colour">
                  {TAG_PALETTE.map((c) => (
                    <button
                      key={c}
                      type="button"
                      role="radio"
                      aria-checked={newColor === c}
                      aria-label={`Use colour ${c}`}
                      disabled={saving}
                      onClick={() => setNewColor(c)}
                      className={cn(
                        'h-4 w-4 rounded-full transition-transform hover:scale-110',
                        newColor === c
                          ? 'ring-2 ring-offset-1 ring-indigo-500 ring-offset-card'
                          : 'ring-1 ring-border',
                      )}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <button
                type="button"
                data-testid="new-tag-trigger"
                disabled={busy}
                onClick={() => setCreating(true)}
                className={cn(
                  'flex items-center gap-2 w-full px-2.5 py-1 text-[11px] text-left rounded transition-colors',
                  'text-indigo-600 dark:text-indigo-300 hover:bg-accent disabled:opacity-50',
                )}
              >
                <Plus className="h-2.5 w-2.5 shrink-0" />
                <span>New tag</span>
              </button>
            )}
          </div>
      </AnchoredPopover>
      )}
    </div>
  )
}

/** Lightweight glyph re-export so callers can show a tag header icon
 *  without importing lucide directly. */
export { Tag as TagGlyph }
